/**
 * API Client — Axios instance with JWT auth, auto-refresh, and standardized error handling.
 *
 * The backend wraps every successful response in a standard envelope:
 * ```json
 * { "success": true, "data": <payload>, "message": "OK" }
 * ```
 * The response interceptor below unwraps `data` so that `api.get<T>()`
 * and `api.post<T>()` callers receive `T` directly (not the envelope).
 *
 * Error responses are also in a standard envelope:
 * ```json
 * { "success": false, "statusCode": 401, "message": "...", "error": "..." }
 * ```
 * The error interceptor converts them into `ApiError` instances.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { authEndpoints } from './endpoints';
import type { ApiErrorResponse, ApiSuccessResponse } from './types';

// ============================================================
// Token storage
// ============================================================

const TOKEN_STORAGE_KEY = 'fc_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'fc_refresh_token';

export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  },
  setTokens(accessToken: string, refreshToken?: string): void {
    // Guard against storing undefined/null/empty values — these would
    // cause "jwt malformed" errors on subsequent API calls.
    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim() === '') {
      console.error(
        '[tokenStorage] Refusing to store invalid access token:',
        accessToken,
      );
      return;
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
    if (refreshToken && typeof refreshToken === 'string') {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    }
  },
  clearTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  },
  hasToken(): boolean {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    return !!token && token !== 'undefined' && token !== 'null';
  },
};

// ============================================================
// Standardized API error class
// ============================================================

export class ApiError extends Error {
  statusCode: number;
  details?: unknown[];

  constructor(statusCode: number, message: string, error?: string, details?: unknown[]) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.details = details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  static fromResponse(data: ApiErrorResponse): ApiError {
    return new ApiError(data.statusCode, data.message, data.error, data.details);
  }

  static fromAxiosError(err: AxiosError<ApiErrorResponse>): ApiError {
    // Case 1: Server responded with an error status (4xx/5xx)
    if (err.response?.data) {
      const d = err.response.data;
      // The backend error envelope has `statusCode`, `message`, `error`.
      // However, some error responses (e.g. from Swagger or middleware)
      // may use the NestJS default format with `statusCode` and `message`
      // but no `success` field. Handle both.
      if (d.statusCode !== undefined) {
        return new ApiError(d.statusCode, d.message, d.error, d.details);
      }
      // Fallback: treat the raw data as a message
      return new ApiError(err.response.status, typeof d === 'string' ? d : JSON.stringify(d), 'RequestError');
    }
    // Case 2: Server responded with error status but no structured body
    if (err.response) {
      return new ApiError(err.response.status, err.message, 'RequestError');
    }
    // Case 3: Request timed out
    if (err.code === 'ECONNABORTED') {
      return new ApiError(408, 'Request timed out', 'Timeout');
    }
    // Case 4: No response at all — network/CORS/SW issue
    // This is the "Network error" case. It can be caused by:
    //   - Backend server not running or unreachable
    //   - CORS preflight rejection (check browser DevTools Console)
    //   - Service Worker intercepting the request incorrectly
    //   - DNS resolution failure
    //
    // We log detailed diagnostics to the console to help debugging.
    const url = err.config?.url ?? '(unknown URL)';
    const method = (err.config?.method ?? 'GET').toUpperCase();
    const baseURL = err.config?.baseURL ?? '';
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

    console.error(
      `[ApiError] Network error on ${method} ${fullUrl}\n` +
      `  Axios code: ${err.code ?? 'N/A'}\n` +
      `  Possible causes:\n` +
      `    1. Backend server not running at ${baseURL || 'the configured base URL'}\n` +
      `    2. CORS preflight rejected (check browser Console for CORS errors)\n` +
      `    3. Service Worker intercepted the request (check if SW is registered)\n` +
      `    4. Network/firewall blocking the connection\n` +
      `  VITE_API_BASE_URL = ${import.meta.env.VITE_API_BASE_URL ?? '(not set, using /api/v1 fallback)'}`,
    );

    return new ApiError(
      0,
      'Network error — please check your connection. ' +
      'If the problem persists, check the browser console for details.',
      'NetworkError',
    );
  }

  get isAuthError(): boolean {
    return this.statusCode === 401 || this.statusCode === 403;
  }
  get isNotFound(): boolean {
    return this.statusCode === 404;
  }
  get isValidation(): boolean {
    return this.statusCode === 400 || this.statusCode === 422;
  }
  get isServer(): boolean {
    return this.statusCode >= 500;
  }
  get isNetworkError(): boolean {
    return this.statusCode === 0;
  }
}

// ============================================================
// Axios instance
// ============================================================

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'Application/json',
  },
  withCredentials: true,
});

// ============================================================
// Request interceptor: attach JWT
// ============================================================

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenStorage.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================
// Response interceptor (success): unwrap standard envelope
// ============================================================
//
// The backend's TransformInterceptor wraps every successful response
// in { success: true, data: <payload>, message: "OK" }.
// We unwrap `data` here so that `api.get<T>()` / `api.post<T>()`
// callers receive `T` directly.
//
// If the response does NOT have the standard envelope (e.g. blob
// responses, streamed data, or third-party APIs), we pass it through
// unchanged.
//
// IMPORTANT: We modify `response.data` in-place so that the convenience
// methods (`.then((r) => r.data)`) return the unwrapped payload.

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const body = response.data;

    // Check if the response matches the standard success envelope:
    //   { success: true, data: ..., message: "..." }
    if (
      body &&
      typeof body === 'object' &&
      'success' in body &&
      body.success === true &&
      'data' in body
    ) {
      // Unwrap: replace response.data with the inner payload
      response.data = (body as ApiSuccessResponse).data;
    }

    return response;
  },
  // Error handler is defined below (combined with auto-refresh logic)
  undefined,
);

// ============================================================
// Response interceptor (error): standardized error + auto-refresh
// ============================================================

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let onLogoutCallback: (() => void) | null = null;

/** Set a callback to be called when tokens are invalidated (redirect to login) */
export function setOnLogoutCallback(cb: () => void): void {
  onLogoutCallback = cb;
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 *
 * Uses `apiClient` (not raw `axios`) so that the response interceptor
 * unwraps the standard envelope `{ success: true, data: { accessToken, ... } }`
 * correctly.
 */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;

  try {
    // Use apiClient so the response interceptor unwraps the envelope.
    // The interceptor transforms `{ success: true, data: { accessToken, ... } }`
    // into `{ accessToken, refreshToken, ... }` (the inner `data` field).
    const tokenData = await apiClient
      .post<{ accessToken: string; refreshToken: string }>(
        authEndpoints.refresh,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      )
      .then((r) => r.data);

    if (!tokenData?.accessToken) {
      console.error('[refreshAccessToken] No accessToken in refresh response:', tokenData);
      tokenStorage.clearTokens();
      if (onLogoutCallback) onLogoutCallback();
      return null;
    }

    tokenStorage.setTokens(tokenData.accessToken, tokenData.refreshToken);
    return tokenData.accessToken;
  } catch {
    tokenStorage.clearTokens();
    if (onLogoutCallback) onLogoutCallback();
    return null;
  }
}

apiClient.interceptors.response.use(
  undefined, // Success handler already defined above
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // 401 → attempt refresh once (unless already on auth endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/') &&
      tokenStorage.getRefreshToken()
    ) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken().finally(() => {
          isRefreshing = false;
        });
      }

      const newToken = await refreshPromise;
      if (newToken && originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      }
      // refresh failed → let ApiError propagate
    }

    return Promise.reject(ApiError.fromAxiosError(error));
  },
);

// ============================================================
// Convenience typed methods
// ============================================================
//
// These methods return `r.data` which, after the response interceptor
// unwrapping, is the inner payload `T` (not the envelope).

export const api = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.get<T>(url, config).then((r) => r.data);
  },
  post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.post<T>(url, body, config).then((r) => r.data);
  },
  put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.put<T>(url, body, config).then((r) => r.data);
  },
  patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.patch<T>(url, body, config).then((r) => r.data);
  },
  delete<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.delete<T>(url, config).then((r) => r.data);
  },
  getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    return apiClient
      .get<Blob>(url, { ...config, responseType: 'blob' })
      .then((r) => r.data);
  },
};

export default api;